import { useCallback, useState } from "react";
import { BsXCircle } from "react-icons/bs";
import { toast } from "react-toastify";
import { useCalendarEventsInRange } from "../../hooks/use-calendar-events-in-range";
import { useLocalAppConfig } from "../../hooks/use-local-app-config";
import { getEndNextCalendarMonthIsh } from "../../lib/get-end-next-calendar-month-ish";
import { getStartPreviousCalendarMonthIsh } from "../../lib/get-start-previous-calendar-month-ish";
import { isEventAfterToday } from "../../lib/is-event-after-today";
import { isEventBeforeToday } from "../../lib/is-event-before-today";
import { isEventOccurringToday } from "../../lib/is-event-occurring-today";
import EventCard from "../EventCard/EventCard";
import { InlineLoadingIcon } from "../InlineLoadingIcon/InlineLoadingIcon";
import "./CalendarConversation.scss";

interface CalendarConversationProps {
  onClose: () => void;
}

const CalendarConversation = ({ onClose }: CalendarConversationProps) => {
  const { accessToken } = useLocalAppConfig();
  const [renderEvents, setRenderEvents] = useState(false);

  const startDate = getStartPreviousCalendarMonthIsh();
  const endDate = getEndNextCalendarMonthIsh();
  const { events, loading: loadingEvents } = useCalendarEventsInRange({
    accessToken,
    startDate,
    endDate,
  });

  const [composerText, setComposerText] = useState("");
  const handleComposerChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setComposerText(e.target.value);
    },
    [setComposerText],
  );

  const handlePromptSubmission = useCallback(() => {
    console.log("prompt submission", composerText);
    toast.error("Prompt submitted, this is not implemented yet");
    setRenderEvents(true);
  }, [composerText]);

  const handleComposerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !!e.metaKey) {
        e.preventDefault();
        handlePromptSubmission();
      }
    },
    [handlePromptSubmission],
  );

  return (
    <div className="calendar-conversation-container">
      <div className="header">
        <div className="header-title">Calendar Conversation</div>
        <div className="header-close" onClick={onClose}>
          <BsXCircle />
        </div>
      </div>
      {loadingEvents && <InlineLoadingIcon />}
      <div className="content">
        <div className="composer">
          <textarea
            onChange={handleComposerChange}
            onKeyDown={handleComposerKeyDown}
            value={composerText}
            placeholder={`Ask us about the ${events.length} events loaded...`}
          />
          <div className="composer-actions">
            <button
              className="composer-action-button btn btn-primary"
              onClick={handlePromptSubmission}
            >
              Pow
            </button>
          </div>
        </div>
        {renderEvents && (
          <div className="events-container">
            {(() => {
              let beforeTodayHeaderShown = false;
              let todayHeaderShown = false;
              let afterTodayHeaderShown = false;

              return events
                .map((event) => {
                  if (!beforeTodayHeaderShown && isEventBeforeToday(event)) {
                    beforeTodayHeaderShown = true;
                    return [
                      <div key="before-today" className="milestone">
                        <div className="events-container-header-title">
                          Past Events
                        </div>
                      </div>,
                      <EventCard key={event.id} event={event} />,
                    ];
                  }
                  if (!todayHeaderShown && isEventOccurringToday(event)) {
                    todayHeaderShown = true;
                    return [
                      <div key="occurring-today" className="milestone">
                        <div className="events-container-header-title">
                          Today
                        </div>
                      </div>,
                      <EventCard key={event.id} event={event} />,
                    ];
                  }
                  if (!afterTodayHeaderShown && isEventAfterToday(event)) {
                    afterTodayHeaderShown = true;
                    return [
                      <div key="after-today" className="milestone">
                        <div className="events-container-header-title">
                          Future Events
                        </div>
                      </div>,
                      <EventCard key={event.id} event={event} />,
                    ];
                  }

                  return <EventCard key={event.id} event={event} />;
                })
                .flat();
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarConversation;
